import React, { useEffect, useState } from 'react'

import { Button } from '../../../../../../../../components/New/Button/Button'
import { DDSTypography } from 'den-design-system'
import { Download } from '@carbon/icons-react'
import NoRecord from '../../../../../../../../components/common/NoContent/NoRecord'
import NoRecordImage from '../../../../../../../../assets/images/setting.svg'
import Spinner from '../../../../../../../../components/common/Spinner/Spinner'
import Table from '../../../../../../../../components/common/DDS/Table/Table'
import { ToastMessageConstants } from '../../../../../../../../constants/ToastMessageConstants'
import ToastNotification from '../../../../../../../../components/common/DDS/Toast/Toast'
import { getAllQueryParamsAsObjectFromUrl } from '../../../../../../../../utils/UrlUtils'
import { getStripeInvoices } from '../../../../../../../../api/stripe/stripe.service'
import { observer } from 'mobx-react-lite'
import pallete from '../../../../../../../../global/newPallete'
import { useSearchParams } from 'react-router-dom'
import { useStoreContext } from '../../../../../../../../store/StoreContext'

const BillingInvoices: React.FC = () => {
  const store = useStoreContext()
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [searchParams, setSearchParams] = useSearchParams()
  const [isInvoiceLoading, setIsInvoiceLoading] = useState<boolean>(false)
  const [stripeInvoiceData, setStripeInvoiceData] = useState<any>()
  const fetchInvoiceDetails = () => {
    setIsInvoiceLoading(true)
    getStripeInvoices({
      $customerId: store.userStore.getCustomerStripeId()
    })
      .then((response: any) => {
        setStripeInvoiceData(response.data.data)
      })
      .catch((e: any) => {
        ToastNotification({
          type: 'error',
          message: ToastMessageConstants.STRIPE
        })
      })
      .finally(() => {
        setIsInvoiceLoading(false)
      })
  }

  useEffect(() => {
    if (store.userStore.getCustomerStripeId()) {
      fetchInvoiceDetails()
    }
  }, [store, stripeInvoiceData])

  const columns = [
    {
      columnHeaderStyle: { width: '260px' },
      columnDataStyle: { width: '260px' },
      heading: (
        <div className='flex justify-start text-start row items-start'>
          <DDSTypography.Paragraph
            size='caption'
            variant='semiBold'
            color={pallete.colors.textDark6}
          >
            Invoice Id
          </DDSTypography.Paragraph>
        </div>
      ),
      dataLabel: 'invoice_id',
      render: (text: any) => (
        <div className='flex justify-start text-start row items-start max-w-[120px]'>
          <div>
            <DDSTypography.Paragraph
              size='para'
              variant='medium'
              color={pallete.colors.textDark4}
              className='tooltip-container sm:block overflow-hidden'
            >
              {text || text === 0 ? text : '-'}
            </DDSTypography.Paragraph>
          </div>
        </div>
      )
    },
    {
      heading: (
        <div className='flex justify-center text-center row items-center'>
          <DDSTypography.Paragraph
            size='caption'
            variant='semiBold'
            color={pallete.colors.textDark6}
          >
            Plan Type
          </DDSTypography.Paragraph>
        </div>
      ),
      dataLabel: 'amount',
      render: (text: any) => (
        <div className='flex justify-center text-center row items-center'>
          <DDSTypography.Paragraph
            size='para'
            variant='medium'
            color={pallete.colors.textDark4}
            className='tooltip-container sm:block overflow-hidden truncate'
          >
            {(() => {
              const numericAmount = parseFloat(
                text?.replace(/[^0-9.]/g, '') || '0'
              )

              if (numericAmount === 0) {
                return 'Trial period'
              } else if (numericAmount > 0 && numericAmount <= 199) {
                return 'Basic Plan'
              } else if (numericAmount > 199) {
                return 'Enterprise Plan'
              } else {
                return text
              }
            })()}
          </DDSTypography.Paragraph>
        </div>
      )
    },
    {
      heading: (
        <div className='flex justify-center text-center row items-center'>
          <DDSTypography.Paragraph
            size='caption'
            variant='semiBold'
            color={pallete.colors.textDark6}
          >
            Billing Period
          </DDSTypography.Paragraph>
        </div>
      ),
      dataLabel: 'billing_period',
      render: (text: any) => (
        <div className='flex justify-center text-center row items-center'>
          <DDSTypography.Paragraph
            size='para'
            variant='medium'
            color={pallete.colors.textDark4}
            className='tooltip-container sm:block overflow-hidden '
          >
            {text}
          </DDSTypography.Paragraph>
        </div>
      )
    },

    {
      heading: (
        <div className='flex justify-center text-center row items-center'>
          <DDSTypography.Paragraph
            size='caption'
            variant='semiBold'
            color={pallete.colors.textDark6}
          >
            Amount
          </DDSTypography.Paragraph>
        </div>
      ),
      dataLabel: 'amount',
      render: (text: any) => (
        <div className='flex justify-center text-center row items-center'>
          <DDSTypography.Paragraph
            size='para'
            variant='medium'
            color={pallete.colors.textDark4}
            className='tooltip-container sm:block overflow-hidden truncate'
          >
            {text}
          </DDSTypography.Paragraph>
        </div>
      )
    },
    {
      heading: (
        <div className='flex justify-center text-center row items-center'>
          <DDSTypography.Paragraph
            size='caption'
            variant='semiBold'
            color={pallete.colors.textDark6}
          >
            Paid Date
          </DDSTypography.Paragraph>
        </div>
      ),
      dataLabel: 'paid_date',
      render: (text: any) => (
        <div className='flex justify-center text-center row items-center'>
          <DDSTypography.Paragraph
            size='para'
            variant='medium'
            color={pallete.colors.textDark4}
            className='tooltip-container sm:block overflow-hidden truncate'
          >
            {text}
          </DDSTypography.Paragraph>
        </div>
      )
    },

    {
      columnHeaderStyle: { width: '56px' },
      columnDataStyle: { width: '56px' },
      heading: (
        <div className='flex justify-center text-center row items-center'>
          <DDSTypography.Paragraph
            size='caption'
            variant='semiBold'
            color={pallete.colors.textDark6}
          >
            Status
          </DDSTypography.Paragraph>
        </div>
      ),
      dataLabel: 'status',
      render: (text: any) => (
        <div
          className={`flex w-auto items-center whitespace-nowrap max-h-[25px] justify-center py-[4px] px-[12px] text-center rounded-lg`}
          style={{
            backgroundColor:
              text === 'Failed'
                ? pallete.colors.warningRed5
                : text === 'Incomplete'
                  ? pallete.colors.warningBgOrange
                  : pallete.colors.warningGreenBg
          }}
        >
          <DDSTypography.Paragraph
            size='para'
            variant='medium'
            color={
              text === 'Failed'
                ? pallete.colors.warningRed2
                : text === 'Incomplete'
                  ? pallete.colors.warningOrange
                  : pallete.colors.warningGreen
            }
            className='tooltip-container sm:block overflow-hidden'
          >
            {text}
          </DDSTypography.Paragraph>
        </div>
      )
    },
    {
      columnHeaderStyle: { width: '5%' },
      columnDataStyle: { width: '5%' },
      heading: '',
      dataLabel: 'invoice_url',
      render: (text: string) => (
        <div className='flex justify-center cursor-pointer'>
          <Button
            id='right-btn'
            type='ghost'
            startIcon={<Download size={16} color={pallete.colors.FillButton} />}
            onClick={() => {
              if (!text) return

              const link = document.createElement('a')
              link.href = text
              link.target = '_blank'
              link.download = `invoice.pdf`
              document.body.appendChild(link)
              link.click()
              document.body.removeChild(link)
            }}
          />
        </div>
      )
    }
  ]

  return (
    <>
      {isInvoiceLoading ? (
        <div>
          <Spinner />
        </div>
      ) : stripeInvoiceData && stripeInvoiceData.length > 0 ? (
        <div className='flex pt-[24px]'>
          <Table
            tableStyle={{ borderColor: pallete.colors.stroke2 }}
            data={stripeInvoiceData}
            columns={columns}
            totalCount={stripeInvoiceData.length}
            rowsPerPage={10}
            currentPage={currentPage}
            onPageChange={(page: number) => {
              const allQueryParams = getAllQueryParamsAsObjectFromUrl(
                location.search
              )
              setSearchParams({ ...allQueryParams, page: page.toString() })
              setCurrentPage(page)
            }}
          />
        </div>
      ) : (
        <NoRecord
          imageSrc={NoRecordImage}
          style={{ height: '65vh' }}
          text={<div>No invoice found</div>}
        />
      )}
    </>
  )
}

export default observer(BillingInvoices)
